import { Button, Card, DatePicker, Form, InputNumber } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import {
  buttonLabel,
  cardConfig,
  dateFormat,
  formConfig,
  messages,
  payMethods,
  placeHolders,
} from '../../../../constants';
import { CancelButton, CustomSelect } from '../../../uiComponents';
import {
  createConsignmentOrderBilling,
  getConsignmentOrderBillingsById,
  updateConsignmentOrderBilling,
} from '../../../../appRedux/actions';
import { ConsignmentOrderMovement } from '../ConsignmentOrderMovement';
import {
  consignOrderBillingConst,
  payStatus,
} from '../../../../constants/ConsignOrderBilling.const';

export const ConsignmentOrderBillingForm = ({
  consignmentOrderId,
  numberOrder,
  consignmentOrderBillingId,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [formDisabled, setFormDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isEditMode = Boolean(consignmentOrderBillingId);
  const url = `/dashboard/factura-de-consigna/${consignmentOrderId}/${numberOrder}`;
  const { consignmentOrderBillingById } = useSelector(
    (state) => state.consignmentOrderBilling
  );
  const isPaid =
    consignmentOrderBillingById?.status === consignOrderBillingConst.paid;
  const movements = Form.useWatch('movements', form);

  const hasProducts = Object.values(movements || {}).some(
    (quantity) => Number(quantity) > 0
  );

  //Obtener la factura de consigna por ID para editar
  useEffect(() => {
    if (isEditMode) {
      dispatch(getConsignmentOrderBillingsById(consignmentOrderBillingId));
    }
  }, [consignmentOrderBillingId, dispatch, isEditMode]);

  //Mapear el dato en el form
  useEffect(() => {
    if (
      isEditMode &&
      consignmentOrderBillingById &&
      consignmentOrderBillingById.consignmentOrderBillingProduct
    ) {
      form.setFieldsValue({
        payMethod: consignmentOrderBillingById.payMethod,
        paidAt: consignmentOrderBillingById.paidAt
          ? moment(consignmentOrderBillingById.paidAt)
          : null,
        status: consignmentOrderBillingById.status,
        total: consignmentOrderBillingById.total,
        movements:
          consignmentOrderBillingById.consignmentOrderBillingProduct.reduce(
            (acc, item) => {
              acc[item.consignmentOrderProduct.id] = item.quantity;
              return acc;
            },
            {}
          ),
      });
    }
  }, [isEditMode, consignmentOrderBillingById, form]);

  useEffect(() => {
    setFormDisabled(isEditMode && isPaid);
  }, [isEditMode, isPaid]);

  const onSubmit = async (values) => {
    setIsLoading(true);

    const items = Object.entries(values.movements || {})
      .filter(([_, quantity]) => quantity > 0)
      .map(([consignmentOrderProductId, quantity]) => ({
        consignmentOrderProductId,
        quantity,
      }));

    const payload = {
      consignmentOrderId,
      payMethod: values.payMethod,
      paidAt: values.paidAt
        ? moment(values.paidAt).format(dateFormat)
        : undefined,
      status: values.status,
      total: Number(values.total),
      items,
    };

    let result;
    if (isEditMode) {
      result = await dispatch(
        updateConsignmentOrderBilling(consignmentOrderBillingId, payload)
      );
    } else {
      result = await dispatch(createConsignmentOrderBilling(payload));
    }

    if ([200, 201].includes(result)) {
      navigate(url);
    }

    setIsLoading(false);
  };

  return (
    <Card size="small" bordered style={cardConfig}>
      <Form
        id="onSubmitConsignment"
        autoComplete="off"
        onFinish={onSubmit}
        form={form}
        disabled={formDisabled}
      >
        <Form.Item
          {...formConfig}
          name="payMethod"
          label="Forma de pago"
          rules={[
            {
              required: true,
              message: messages.required('Forma de pago'),
            },
          ]}
        >
          <CustomSelect
            placeholder={placeHolders.payMethod}
            options={payMethods.map(({ value, label }) => ({
              value,
              label,
            }))}
            disabled={consignmentOrderBillingId}
          />
        </Form.Item>

        <Form.Item
          {...formConfig}
          name="paidAt"
          label="Pagado en"
          rules={
            isEditMode
              ? [
                  {
                    required: true,
                    message: messages.required('Pagado en'),
                  },
                ]
              : []
          }
        >
          <DatePicker style={{ width: '100%' }} format={dateFormat} />
        </Form.Item>

        <Form.Item
          {...formConfig}
          name="status"
          label="Estado de pago"
          rules={
            isEditMode
              ? [
                  {
                    required: true,
                    message: messages.required('Estado de pago'),
                  },
                ]
              : []
          }
        >
          <CustomSelect
            placeholder={placeHolders.payState}
            options={payStatus}
          />
        </Form.Item>

        <Form.Item
          {...formConfig}
          name="total"
          label="Total"
          rules={[{ required: true, message: messages.required('Total') }]}
        >
          <InputNumber placeholder="Total" />
        </Form.Item>

        <div style={{ marginTop: 30 }}>
          <ConsignmentOrderMovement
            ConsignmentOrderId={consignmentOrderId}
            disabled={isEditMode}
          />
        </div>

        <div style={{ marginTop: 20 }}>
          <Button
            type="primary"
            htmlType="submit"
            className="btn-form"
            disabled={isLoading || formDisabled || !hasProducts}
          >
            {buttonLabel.save}
          </Button>
        </div>
      </Form>
      <CancelButton path={url} />
    </Card>
  );
};
